import React, { useState } from 'react';
import { Box, Typography, Button, Divider, Popover, Checkbox, RadioGroup, FormControlLabel, Radio, TextField } from '@mui/material';
import { ArrowDownAZ, ArrowUpAZ, Search } from 'lucide-react';
import Toggle from '../ui/toggle';

export interface StatusOption {
    id: string;
    label: string;
    // Optional - only the invoices page's Status column supplies these
    // (see statusBreakdown in invoice-controller.js). Other callers reusing
    // this same popover (category, recurrence type, etc.) simply omit them.
    count?: number;
    totalAmount?: string;
}

export interface StatusFilterApplyResult {
    selectedIds: string[];
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
}

interface StatusFilterProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    onApply: (result: StatusFilterApplyResult) => void;
    options: StatusOption[];
    // Currently-checked (visible) status ids.
    selectedIds: string[];
    // Lets this same checkbox-list popover double as a plain multi-select
    // filter (e.g. category, recurrence type) where the semantics are
    // "checked = included in filter" rather than "checked = visible".
    title?: string;
    // Every other filterable column on these pages (description/date/amount)
    // embeds its own sort control in the same popover so there's ever only
    // one header button per column - set this to give this column the same
    // uniform single-button treatment instead of a separate sort arrow.
    sortByValue?: string;
    sortFieldLabel?: string;
    currentSort?: { sortBy?: string; sortDirection?: 'ASC' | 'DESC' };
    // Adds a text box above the checkbox list to narrow long option lists
    // (e.g. categories) by label. Off by default since short fixed lists
    // (status, recurrence type) don't need it.
    searchable?: boolean;
    searchPlaceholder?: string;
    // Renders the same controls inline (no Popover/anchor) for use inside a
    // combined modal instead of an anchored popup.
    embedded?: boolean;
    // Hides this section's own Apply Now/Clear buttons - used when a parent
    // (the combined filter modal) drives Apply/Clear for every section at
    // once via the imperative handle below instead.
    hideActions?: boolean;
}

export type StatusFilterHandle = {
    applyNow: () => void;
    clear: () => void;
};

// Show/hide by status - a plain checkbox list. Checked = visible, unchecked = hidden.
const StatusFilter = React.forwardRef<StatusFilterHandle, StatusFilterProps>(({ anchorEl, open, onClose, onApply, options, selectedIds, title = 'Show / Hide Status', sortByValue, sortFieldLabel, currentSort, searchable = false, searchPlaceholder = 'search', embedded = false, hideActions = false }, ref) => {
    const [localSelected, setLocalSelected] = useState<string[]>(selectedIds);
    const [searchTerm, setSearchTerm] = useState<string>('');
    // A filter is "active" whenever some (not all) options are currently
    // selected - matches the Filter By X toggle pattern used by the other
    // column filters (description/date/amount), which this list previously
    // lacked, making it unclear a filter existed here at all.
    const [filterEnabled, setFilterEnabled] = useState<boolean>(selectedIds.length < options.length);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | ''>(
        currentSort?.sortBy === sortByValue ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : ''
    );

    React.useEffect(() => {
        setLocalSelected(selectedIds);
        setSearchTerm('');
        setFilterEnabled(selectedIds.length < options.length);
        setSortOrder(currentSort?.sortBy === sortByValue ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : '');
    }, [selectedIds, open, currentSort, sortByValue, options.length]);

    const visibleOptions = searchable && searchTerm
        ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
        : options;

    const allSelected = visibleOptions.length > 0 && visibleOptions.every(o => localSelected.includes(o.id));
    const someSelected = visibleOptions.some(o => localSelected.includes(o.id));

    const toggleOption = (id: string) => {
        setLocalSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Scoped to the currently visible (search-filtered) options, so
    // "Select All" while searching doesn't silently select unrelated items.
    const toggleAll = () => {
        if (allSelected) {
            setLocalSelected(prev => prev.filter(id => !visibleOptions.some(o => o.id === id)));
        } else {
            setLocalSelected(prev => Array.from(new Set([...prev, ...visibleOptions.map(o => o.id)])));
        }
    };

    const handleApplyNow = () => onApply({
        selectedIds: filterEnabled ? localSelected : options.map(o => o.id),
        sortBy: sortOrder ? sortByValue : undefined,
        sortDirection: sortOrder ? (sortOrder.toUpperCase() as 'ASC' | 'DESC') : undefined,
    });

    const handleClear = () => {
        setFilterEnabled(false);
        onApply({
            selectedIds: options.map(o => o.id),
            sortBy: sortOrder ? sortByValue : undefined,
            sortDirection: sortOrder ? (sortOrder.toUpperCase() as 'ASC' | 'DESC') : undefined,
        });
    };

    React.useImperativeHandle(ref, () => ({
        applyNow: handleApplyNow,
        clear: handleClear,
    }));

    const content = (
        <Box sx={{ p: embedded ? 0 : 2, borderRadius: 3, background: embedded ? 'transparent' : 'var(--color-page-background)', width: embedded ? '100%' : 260 }}>
                {!embedded && <Typography fontWeight={600} fontSize={16} mb={1}>{title}</Typography>}
                {!embedded && <Divider sx={{ mb: 1 }} />}

                {sortByValue && (
                    <>
                        <Typography sx={{ fontSize: 14, mb: 0.5 }}>Sort By {sortFieldLabel}</Typography>
                        <RadioGroup value={sortOrder || ''} onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')} sx={{ mb: 1, pl: 1 }}>
                            <FormControlLabel
                                value="asc"
                                sx={{ height: 24 }}
                                control={<Radio size="small" />}
                                label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownAZ size={14} />Ascending</Typography>}
                            />
                            <FormControlLabel
                                value="desc"
                                sx={{ height: 24 }}
                                control={<Radio size="small" />}
                                label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowUpAZ size={14} />Descending</Typography>}
                            />
                        </RadioGroup>
                        <Divider sx={{ mb: 1 }} />
                    </>
                )}

                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Toggle checked={filterEnabled} onChange={setFilterEnabled} />
                    <Typography sx={{ fontSize: 14 }}>Filter By {sortFieldLabel || title.replace(/^Filter by /i, '')}</Typography>
                </Box>

                {filterEnabled && (
                    <>
                        {searchable && (
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                sx={{ mb: 1 }}
                                InputProps={{
                                    sx: { fontSize: 12 },
                                    startAdornment: <Search size={16} color="var(--color-primary)" style={{ marginRight: 8 }} />,
                                }}
                            />
                        )}

                        <Box
                            display="flex" alignItems="center" px={0.5} py={0.25}
                            sx={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                            onClick={toggleAll}
                        >
                            <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={!allSelected && someSelected}
                                onChange={toggleAll}
                                onClick={e => e.stopPropagation()}
                                sx={{ p: 0.5, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, '&.MuiCheckbox-indeterminate': { color: 'var(--color-primary)' } }}
                            />
                            <Typography fontSize={12} fontWeight={600} color="var(--color-primary)">Select All</Typography>
                        </Box>

                        <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                            {visibleOptions.map(option => (
                                <Box
                                    key={option.id}
                                    display="flex" alignItems="center" justifyContent="space-between" px={0.5}
                                    sx={{ cursor: 'pointer', '&:hover': { background: 'var(--color-container-background)' }, borderBottom: '1px solid #f5f5f5' }}
                                    onClick={() => toggleOption(option.id)}
                                >
                                    <Box display="flex" alignItems="center" minWidth={0}>
                                        <Checkbox
                                            size="small"
                                            checked={localSelected.includes(option.id)}
                                            onChange={() => toggleOption(option.id)}
                                            onClick={e => e.stopPropagation()}
                                            sx={{ p: 0.5, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                        />
                                        <Typography fontSize={13} noWrap>{option.label} ({option.count ?? 0})</Typography>
                                    </Box>
                                    {option.totalAmount != null && (
                                        <Typography fontSize={11} color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap', pl: 1 }}>
                                            {option.totalAmount}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                            {visibleOptions.length === 0 && (
                                <Typography fontSize={12} color="text.secondary" sx={{ px: 1, py: 1 }}>No matches found</Typography>
                            )}
                        </Box>
                    </>
                )}

                {!hideActions && (
                    <Box display="flex" gap={1} mt={2}>
                        <Button
                            variant="contained"
                            sx={{ flex: 1, minWidth: 0, px: 1, borderRadius: 2, background: 'var(--color-primary)', color: '#fff', fontWeight: 600, boxShadow: 'none', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-primary)' } }}
                            onClick={handleApplyNow}
                        >
                            Apply Now
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{ flex: 1, minWidth: 0, px: 1, borderRadius: 2, fontWeight: 600, background: 'var(--color-container-background)', color: 'var(--color-primary)', borderColor: 'var(--color-primary)', boxShadow: 'none', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { background: 'var(--color-container-background)', borderColor: 'var(--color-primary)' } }}
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    </Box>
                )}
            </Box>
    );

    if (embedded) {
        return content;
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' } }}
        >
            {content}
        </Popover>
    );
});

export default StatusFilter;
